import Flex from "common/Boxes/Flex";
import IconWithFallback from "Global/IconWithFallback";
import { ICON_TYPES } from "@shared/common/constants";
import Text from "common/Typography/Text";
import { typography } from "@shared/common/Themes/typography";
import React, { useContext } from "react";
import Col from "antd/es/col";
import Row from "antd/es/row";
import { ConsolidatedCoinDataWithKey } from "@shared/types";
import { RootState } from "app/store";
import { PrecisionConfig } from "types";
import styled, { css, useTheme } from "styled-components";
import { newLookListCard } from "features/portfolio/NewLook/styles.ts";
import AutoSizeText from "common/Text/AutoSizeText";
import { COIN_CARD_COLUMN_SPANS } from "features/portfolio/common/constants.ts";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import Card from "common/Boxes/Card.tsx";
import CoinActionsDropdown from "features/portfolio/common/CoinActionsDropdown.tsx";
import useAssetItem from "features/portfolio/common/useAssetItem.tsx";
import { useIsSubUser, useSelectUser } from "@shared/utils/hooks/misc.ts";
import NewLookCoinCard from "features/portfolio/NewLook/NewLookCoinCard.tsx";

interface CoinCardProps {
  coinData: ConsolidatedCoinDataWithKey;
  precisionSelector: (state: RootState) => PrecisionConfig | undefined;
}

const CoinCard = ({ coinData, precisionSelector }: CoinCardProps) => {
  const { isDrawer } = useContext(DrawerContext) || {};
  const {
    getQuantity,
    getPrice,
    getPriceChange,
    getTotalAmount,
    openCoinExtraDetails,
  } = useAssetItem({ precisionSelector });
  const isSubUser = useIsSubUser();
  const user = useSelectUser();
  const { isNewLook } = useTheme();

  if (isNewLook)
    return (
      <NewLookCoinCard
        coinData={coinData}
        precisionSelector={precisionSelector}
      />
    );

  return (
    <StyledCard onClick={() => openCoinExtraDetails(coinData)}>
      <Row style={{ alignItems: "center" }}>
        <Col span={COIN_CARD_COLUMN_SPANS[0]}>
          <Flex
            $justifyContent="flex-start"
            style={{ width: "100%", paddingRight: 8 }}
          >
            {isNewLook ? (
              <IconWithFallback
                iconType={ICON_TYPES.COIN}
                coinOrExchangeName={coinData.symbol}
                pxIconSize={28}
              />
            ) : (
              <IconWithFallback
                iconType={ICON_TYPES.COIN}
                coinOrExchangeName={coinData.symbol}
                iconSize="l"
              />
            )}
            <CoinWrapper>
              <AutoSizeText>{coinData.symbol}</AutoSizeText>
              <Text as="div" type="secondary">
                {getPrice(coinData)}
              </Text>
            </CoinWrapper>
          </Flex>
        </Col>
        <Col span={COIN_CARD_COLUMN_SPANS[1]}>
          <Flex $flexDirection="column" $alignItems="flex-start">
            <Text as="div">{getTotalAmount(coinData)}</Text>
            <Text as="div" type="secondary" $size={typography.fontSizes.S}>
              {getQuantity(coinData.quantity, coinData.symbol)}
            </Text>
          </Flex>
        </Col>
        <Col span={COIN_CARD_COLUMN_SPANS[2]}>
          <Flex style={{ width: "100%" }}>
            <PriceChangeWrapper>{getPriceChange(coinData)}</PriceChangeWrapper>
            {!isDrawer && !isSubUser && !user?.isWhopExperience() && (
              <CoinActionsDropdown coinData={coinData} />
            )}
          </Flex>
        </Col>
      </Row>
    </StyledCard>
  );
};

export default React.memo(CoinCard);

const CoinWrapper = styled(Flex)`
  margin-left: 12px;
  width: 100%;
  overflow: hidden;
  flex-direction: column;
  align-items: flex-start;
`;
const StyledCard = styled(Card)`
  padding: 8px 12px;
  cursor: pointer;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      padding: 10px 14px;
      ${newLookListCard};
      ${CoinWrapper} > :first-child {
        color: ${p.theme.colors.titleColor};
        font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
      }
    `}
`;
const PriceChangeWrapper = styled.div`
  flex: 1;
  min-width: 0;
  height: 52px;
`;
